'use client';
import { useEffect, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { Button, FormError } from '@/components/site/ui';
import { Field, Input } from '@/components/ui/field';
import { confirmBookingCode, resendBookingCode } from './booking-actions';

/**
 * The WhatsApp code that turns the filled form into a request for the clinic (owner decision,
 * 2026-11-02). Until it is confirmed nothing reaches the clinic and the time stays free.
 */
export function BookingCodeStep({
  slug,
  verificationId,
  phoneEnding,
  resendAfter,
  onConfirmed,
  onChangePhone,
  onSlotTaken,
}: {
  slug: string;
  verificationId: string;
  phoneEnding: string;
  resendAfter: string;
  onConfirmed: (requestId: string) => void;
  onChangePhone: () => void;
  /** The time was taken by someone else while the code was being typed. */
  onSlotTaken: () => void;
}) {
  const t = useTranslations('clinicBooking');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [until, setUntil] = useState(() => Date.parse(resendAfter));
  const [now, setNow] = useState(() => Date.now());
  const [pending, startTransition] = useTransition();
  const wait = Math.max(0, Math.ceil((until - now) / 1000));

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [wait]);

  function confirm(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setNotice('');
    startTransition(async () => {
      const result = await confirmBookingCode(slug, verificationId, code);
      if (result.error === 'conflict') return onSlotTaken();
      if (!result.data) return setError(t(result.error ?? 'failed'));
      onConfirmed(result.data.id);
    });
  }

  function resend() {
    setError('');
    setNotice('');
    startTransition(async () => {
      const result = await resendBookingCode(slug, verificationId);
      if (!result.data) return setError(t(result.error ?? 'failed'));
      setCode('');
      setUntil(Date.parse(result.data.resendAfter));
      setNow(Date.now());
      setNotice(t('codeResent'));
    });
  }

  return (
    <form className="space-y-5" onSubmit={confirm}>
      <p className="text-sm leading-relaxed text-muted-foreground">{t('codeBody', { ending: phoneEnding })}</p>
      <Field label={t('codeLabel')} htmlFor="booking-code">
        <Input
          id="booking-code"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          icon="shield-key"
          placeholder="000000"
          required
          disabled={pending}
          className="tracking-[0.3em]"
        />
      </Field>
      <FormError>{error}</FormError>
      {notice && (
        <p className="text-sm text-muted-foreground" role="status">
          {notice}
        </p>
      )}
      <Button type="submit" block loading={pending} disabled={code.length !== 6}>
        {t('codeConfirm')}
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <Button
          variant="unstyled"
          onClick={onChangePhone}
          disabled={pending}
          className="font-semibold text-muted-foreground underline decoration-dotted decoration-2 underline-offset-[6px]"
        >
          {t('codeChangePhone')}
        </Button>
        <Button
          variant="unstyled"
          onClick={resend}
          disabled={pending || wait > 0}
          className="font-semibold text-medlux underline decoration-dotted decoration-2 underline-offset-[6px] disabled:text-muted-foreground disabled:no-underline"
        >
          {wait > 0 ? t('codeResendIn', { seconds: wait }) : t('codeResend')}
        </Button>
      </div>
    </form>
  );
}
